/**
 * Where each goal stands, in one place, so the Goals screen, the notices on
 * Home and the badge on the navigation all agree.
 *
 *   ready     active, and the pot covers the target: complete it
 *   late      active, past its date, and not saved up: missed, or more time?
 *   saving    active, and neither of those
 *   completed spent, from the pot, with an entry in the journal
 *   missed    a parent said it did not happen; no money moved
 *
 * READY WINS OVER LATE. A goal that is saved up is ready however late it is;
 * the question a parent is asked is "complete it?", not "did it fail?".
 *
 * "Past its date" is judged on THIS DEVICE's calendar, not the server's: a
 * family's day ends at their midnight, and a goal due today is not late until
 * tomorrow.
 */

import type { Goal } from './state';

export type GoalStanding = 'ready' | 'late' | 'saving' | 'completed' | 'missed';

/** The lists the Goals screen can show. `ready` and `late` are active goals. */
export const GOAL_FILTERS = ['active', 'ready', 'late', 'completed', 'missed'] as const;
export type GoalFilter = (typeof GOAL_FILTERS)[number];

/** Today, as YYYY-MM-DD, on this device's own calendar. */
export function localToday(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function standing(goal: Goal, today: string): GoalStanding | null {
  if (goal.status === 'completed') return 'completed';
  if (goal.status === 'missed') return 'missed';
  if (goal.status !== 'active') return null;
  if (goal.pot >= goal.target) return 'ready';
  if (goal.due && goal.due < today) return 'late';
  return 'saving';
}

/** The goals in one list, in the order a parent should deal with them. */
export function inList(goals: readonly Goal[], filter: GoalFilter, today: string): Goal[] {
  const order: Record<GoalStanding, number> = { ready: 0, late: 1, saving: 2, completed: 3, missed: 3 };
  const matches = goals.filter((goal) => {
    const s = standing(goal, today);
    if (!s) return false;
    if (filter === 'active') return s === 'ready' || s === 'late' || s === 'saving';
    return s === filter;
  });
  const closedFirst = (a: Goal, b: Goal) => (b.closed ?? '').localeCompare(a.closed ?? '');
  return filter === 'completed' || filter === 'missed'
    ? matches.sort(closedFirst)
    : matches.sort((a, b) => order[standing(a, today)!] - order[standing(b, today)!]);
}

/** How many are in each list, for the filter chips. */
export function counts(goals: readonly Goal[], today: string): Record<GoalFilter, number> {
  return Object.fromEntries(GOAL_FILTERS.map((f) => [f, inList(goals, f, today).length])) as Record<GoalFilter, number>;
}

/**
 * A goal filter and, optionally, one goal to open, as the `focus` string Home
 * passes to a tab: `ready`, or `ready:<goal id>` to open that goal's question.
 */
export function readFocus(focus: string | null | undefined): { filter: GoalFilter; open: string | null } {
  const [filter, open] = (focus ?? '').split(':');
  return {
    filter: (GOAL_FILTERS as readonly string[]).includes(filter ?? '') ? (filter as GoalFilter) : 'active',
    open: open || null,
  };
}

/** A date for reading: "20 Sep 2026". Noon, so it never slips a day. */
export function readableDate(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
